import { toMilliseconds } from '@art-suite/art-core-ts';
import { DEFAULT_MAX_AGE_MS } from '../QueryCache';
import { CacheEntryMetadata, isCacheEntryExpired, TypedCacheEntry } from './CacheEntry';
import { PersistenceLayerInterface } from './PersistenceLayer';

export interface PersistenceLayerBaseOptions {
  defaultMaxAgeMs?: number;
}

const DEFAULT_OPTIONS: PersistenceLayerBaseOptions = {
  defaultMaxAgeMs: DEFAULT_MAX_AGE_MS,
};

export abstract class PersistenceLayerBase<ValueType = unknown> implements PersistenceLayerInterface<ValueType> {
  constructor(private readonly options: PersistenceLayerBaseOptions = DEFAULT_OPTIONS) {
  }

  get defaultMaxAgeMs() {
    return this.options.defaultMaxAgeMs ?? DEFAULT_MAX_AGE_MS;
  }

  getExpiresAt(expiresAt?: number, now?: number): number {
    return expiresAt ?? toMilliseconds(now) + this.defaultMaxAgeMs;
  }

  // *************************************************************************************
  // To-implement Abstract methods
  // *************************************************************************************
  abstract set(key: string, value: ValueType, expiresAt?: number, createdAt?: number, now?: number): Promise<TypedCacheEntry<ValueType>>;

  abstract getEntry(key: string, now?: number, prefetchOnly?: boolean): Promise<TypedCacheEntry<ValueType> | CacheEntryMetadata | undefined>;

  abstract getMetadata(key: string, now?: number): Promise<CacheEntryMetadata | undefined>;

  abstract has(key: string, now?: number): Promise<boolean>;

  abstract freshen(key: string, expiresAt?: number, now?: number): Promise<boolean>;

  abstract purge(key: string): Promise<boolean>;

  abstract getAllKeys(prefix?: string, now?: number): Promise<string[]>;

  // *************************************************************************************
  // Default, singleton implementations
  // *************************************************************************************
  async get(key: string, now?: number): Promise<ValueType | undefined> {
    const entry = await this.getEntry(key, now);
    return entry?.value;
  }

  // *************************************************************************************
  // Bulk Get Operations - Default, non-optimized implementations, but getAllKeys is required to be implemented by the subclass.
  // *************************************************************************************
  async getAllEntries(prefix?: string, now?: number): Promise<TypedCacheEntry<ValueType>[]> {
    const allKeys = await this.getAllKeys(prefix, now);
    const allEntries: TypedCacheEntry<ValueType>[] = [];
    for (const key of allKeys) {
      const entry = await this.getEntry(key, now);
      if (entry?.value) {
        allEntries.push(entry as TypedCacheEntry<ValueType>);
      }
    }
    return allEntries;
  }

  async getAllMetadata(prefix?: string, now?: number): Promise<CacheEntryMetadata[]> {
    const allKeys = await this.getAllKeys(prefix, now);
    const allMetadata: CacheEntryMetadata[] = [];
    for (const key of allKeys) {
      const metadata = await this.getMetadata(key, now);
      if (metadata) {
        allMetadata.push(metadata);
      }
    }
    return allMetadata;
  }

  // *************************************************************************************
  // Default, non-optimized implementations of bulk update operations
  // *************************************************************************************
  async freshenAll(expiresAt: number | undefined, prefix: string | undefined, now?: number): Promise<number> {
    const allKeys = await this.getAllKeys(prefix, now);
    for (const key of allKeys) {
      await this.freshen(key, expiresAt, now);
    }
    return allKeys.length;
  }

  async purgeAllEntries(prefix?: string, now?: number): Promise<number> {
    const allKeys = await this.getAllKeys(prefix, now);
    for (const key of allKeys) {
      await this.purge(key);
    }
    return allKeys.length;
  }

  async purgeExpiredEntries(now?: number): Promise<number> {
    const allEntries = await this.getAllEntries();
    for (const entry of allEntries) {
      if (isCacheEntryExpired(entry, now)) {
        await this.purge(entry.key);
      }
    }
    return allEntries.length;
  }

  // *************************************************************************************
  // Default, non-optimized implementations of stats operations
  // *************************************************************************************
  async getCacheCount(): Promise<number> {
    const allKeys = await this.getAllKeys();
    return allKeys.length;
  }

  // Exact cache size in UTF-16 chars (sum of all JSON.stringify'd entry lengths).
  async getCacheSize(): Promise<number> {
    const allKeys = await this.getAllKeys();
    let total = 0;
    for (const key of allKeys) {
      const entry = await this.getEntry(key);
      if (entry !== undefined) total += JSON.stringify(entry).length;
    }
    return total;
  }
}
